import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { ConfigService } from '@gallaghercp/core-frontend';
import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app';
import { getMessaging, getToken, Messaging, onMessage } from 'firebase/messaging';

// Firebase throws when any of these are missing, so the config is only usable when all are present.
const REQUIRED_FIREBASE_KEYS = ['apiKey', 'projectId', 'appId', 'messagingSenderId'] as const;

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    private swPush = inject(SwPush);
    private http = inject(HttpClient);
    private platformId = inject(PLATFORM_ID);
    private config = inject(ConfigService);

    private readonly VAPID_PUBLIC_KEY = '2MDO6Ksmvxzw6zhF09gjtxYdIExK58gHnZTl73EqaUc';
    private messaging: Messaging | null = null;
    private firebaseApp: FirebaseApp | null = null;

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.initFirebase();
        }
    }

    requestPermission(): void {
        if (!isPlatformBrowser(this.platformId) || !this.messaging) return;

        getToken(this.messaging, {
            vapidKey: this.VAPID_PUBLIC_KEY,
        })
            .then(token => {
                if (token) {
                    console.log('Push token:', token);
                }
            })
            .catch(err => {
                console.error('FCM token request error:', err);
            });
    }

    receiveMessage(): void {
        if (!isPlatformBrowser(this.platformId) || !this.messaging) return;

        onMessage(this.messaging, payload => {
            console.log('Message received:', payload);
            this.showCustomNotification(payload);
        });
    }

    showCustomNotification(message: any): void {
        if (!isPlatformBrowser(this.platformId)) return;

        const { title, body } = message.notification || {};
        if ('Notification' in window && title && body) {
            new Notification(title, {
                body,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
            });
        }
    }

    subscribeToNotifications(): void {
        if (!isPlatformBrowser(this.platformId)) return;

        this.swPush
            .requestSubscription({
                serverPublicKey: this.urlBase64ToUint8Array(this.VAPID_PUBLIC_KEY) as unknown as string,
            })
            .then(subscription => {
                this.sendSubscriptionToTheServer(subscription);
            })
            .catch(err => console.error('Could not subscribe to notifications', err));
    }

    private initFirebase(): void {
        // Avoid re-initialisation
        if (this.firebaseApp) return;

        const firebaseNotifications: unknown = this.config.get('firebaseNotifications');
        if (!this.isFirebaseConfig(firebaseNotifications)) {
            if (firebaseNotifications) {
                console.warn('Firebase notifications config is incomplete, push notifications are disabled.');
            }
            return;
        }

        try {
            this.firebaseApp = initializeApp(firebaseNotifications);
            this.messaging = getMessaging(this.firebaseApp);
        } catch (err: unknown) {
            this.firebaseApp = null;
            this.messaging = null;
            console.error('Firebase messaging initialisation failed:', err);
        }
    }

    private isFirebaseConfig(value: unknown): value is FirebaseOptions {
        if (typeof value !== 'object' || value === null) return false;

        const config = value as Record<string, unknown>;
        return REQUIRED_FIREBASE_KEYS.every(key => typeof config[key] === 'string' && config[key] !== '');
    }

    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        if (!isPlatformBrowser(this.platformId)) return new Uint8Array();

        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }

        return outputArray;
    }

    private sendSubscriptionToTheServer(subscription: PushSubscription): void {
        this.http.post('/pwa-subscribe', subscription).subscribe();
    }
}
